import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { compact, get, uniq } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Tooltip } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { GridActionsCellItem, GridRenderCellParams } from '@mui/x-data-grid';
import { searchPractitioners, deletePractitioner } from './_api';
import { searchOrganizations } from '../Organizations/_api';
import { IPractitioner } from './_types';
import { IAddress, ITelecom } from '../_types';
import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import useAlerts from 'components/Alerts/useAlerts';
import { MuiGrid } from 'components/MuiGrid/MuiGrid';
import Header from 'components/Header/Header';
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import { useMuiGrid } from 'utils/hooks/useMuiGrid';
import { useEntityRemove } from 'utils/hooks/useEntityRemove';
import { useActions } from 'utils/hooks/useActions';
import {
  getActiveTranslated,
  getAddressTranslated,
  getGenderTranslated,
  getIdentifierTranslated,
  getNameTranslated,
  getOrganizationsTranslated,
  getTelecomTranslated,
} from '../_grid';
import { format } from 'date-fns';
import { useUser } from 'utils/hooks/useUser';

const muiGridKey = 'practitionersMUI';

const Practitioners: React.FC = () => {
  const { t } = useTranslation(['Practitioners', 'PatientRegistry']);
  const navigate = useNavigate();
  const { hasRole } = useUser();
  const hasRolePractitioners = hasRole('ROLE_MANAGE_PRACTITIONERS');
  const { toggleLoader } = useAppGlobals();
  const { addErrorAlert } = useAlerts();
  const { storeConfirmationData } = useActions();
  const { confirmationData } = useAppInfo();
  const { injectColumnWidthsIntoColumns, reorderColumnsByGridSettings } = useMuiGrid(muiGridKey);

  const [practitioners, setPractitioners] = useState<IPractitioner[] | null>(null);

  const loadPractitioners = useCallback(async () => {
    toggleLoader();
    try {
      const [data, orgs] = await Promise.all([searchPractitioners({}), searchOrganizations({})]);
      const resolvedOrgMap: Record<number, string> = {};
      (orgs || []).forEach((o: any) => {
        resolvedOrgMap[o.id] = o.name;
      });
      setPractitioners(
        (data || []).map((item: IPractitioner) => ({
          ...item,
          nameTranslated: getNameTranslated(item.name as any[]),
          activeTranslated: getActiveTranslated(get(item, 'active', false), t),
          identifierTranslated: getIdentifierTranslated(item.identifier as any[]),
          telecomTranslated: getTelecomTranslated(item.telecom as ITelecom[], t),
          addressTranslated: getAddressTranslated(item.address as IAddress[]),
          genderTranslated: getGenderTranslated(item.gender, t),
          organizationsTranslated: getOrganizationsTranslated(item.organizationIds, resolvedOrgMap),
        })),
      );
    } catch (error) {
      addErrorAlert(t('loadError'));
      console.error('Error loading practitioners:', error);
    } finally {
      toggleLoader(false);
    }
  }, [addErrorAlert, t, toggleLoader]);

  const onCustomEntityEdit = useCallback(
    (row: any) => {
      navigate(`/administration/practitioners/${row.id}`);
    },
    [navigate],
  );

  const { onEntityRemove } = useEntityRemove(deletePractitioner, t, loadPractitioners);

  const clearAction = () => {
    storeConfirmationData(null);
  };
  const confirmAction = async () => {
    await onEntityRemove(get(confirmationData, 'id'));
    clearAction();
  };

  useEffect(() => {
    loadPractitioners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const practitionersArray = useMemo(
    () =>
      compact(
        uniq(
          (practitioners || []).map((practitioner: any) =>
            get(practitioner, 'genderTranslated', ''),
          ),
        ),
      ),
    [practitioners],
  );

  const columns = useMemo(
    () =>
      reorderColumnsByGridSettings(
        injectColumnWidthsIntoColumns(
          [
            {
              field: 'actions',
              headerName: t('Grid:actions'),
              type: 'actions',
              width: 100,
              hideable: false,
              renderCell: ({ row }: GridRenderCellParams) => (
                <>
                  <GridActionsCellItem
                    icon={
                      <Tooltip title={t('action.edit')}>
                        <EditIcon />
                      </Tooltip>
                    }
                    label={t('action.edit')}
                    onClick={() => onCustomEntityEdit(row)}
                    data-pw={`practitioner-edit-${row.name}`}
                    disabled={!hasRolePractitioners}
                  />
                  <GridActionsCellItem
                    icon={
                      <Tooltip title={t('action.delete')}>
                        <DeleteIcon />
                      </Tooltip>
                    }
                    label={t('action.delete')}
                    onClick={() => {
                      storeConfirmationData({
                        id: get(row, 'id'),
                        name: get(row, 'nameTranslated'),
                      });
                    }}
                    data-pw={`practitioner-delete-${row.name}`}
                    disabled={!hasRolePractitioners}
                  />
                </>
              ),
            },
            {
              field: 'activeTranslated',
              headerName: t('PatientRegistry:active'),
              type: 'singleSelect',
              valueOptions: [t('yes'), t('no')],
              width: 120,
            },
            { field: 'id', headerName: t('PatientRegistry:id'), type: 'number', width: 100 },
            {
              field: 'nameTranslated',
              headerName: t('name'),
              width: 200,
            },
            {
              field: 'birthDate',
              headerName: t('birthDate'),
              type: 'date',
              valueGetter: (value: any) => value && new Date(value),
              valueFormatter: (value: any) => value && format(value, 'dd. MM. yyyy'),
            },
            {
              field: 'genderTranslated',
              headerName: t('gender'),
              width: 70,
              type: 'singleSelect',
              valueOptions: practitionersArray,
            },
            {
              field: 'identifierTranslated',
              headerName: t('PatientRegistry:identifier'),
              width: 180,
            },
            {
              field: 'telecomTranslated',
              headerName: t('PatientRegistry:contact'),
              width: 180,
            },
            {
              field: 'addressTranslated',
              headerName: t('PatientRegistry:address'),
              width: 200,
            },
            {
              field: 'organizationsTranslated',
              headerName: t('form.organizations'),
              width: 200,
            },
          ],
          200,
        ),
      ),
    [
      hasRolePractitioners,
      injectColumnWidthsIntoColumns,
      onCustomEntityEdit,
      practitionersArray,
      reorderColumnsByGridSettings,
      storeConfirmationData,
      t,
    ],
  );

  return (
    <>
      <Header
        title=""
        addUrl={hasRolePractitioners ? '/administration/practitioners/create' : ''}
      />

      <MuiGrid
        gridKey={muiGridKey}
        rows={practitioners || []}
        columns={columns}
        dataTour="practitionersGrid"
      />

      {confirmationData && confirmationData.id && !confirmationData.editDialog && (
        <ConfirmationDialog
          title={t('deleteConfirm', { name: confirmationData.name })}
          open={true}
          aria-labelledby="form-dialog-title"
          fullWidth={true}
          cancelAction={clearAction}
          confirmAction={confirmAction}
        />
      )}
    </>
  );
};

export default Practitioners;
